import axios from "axios";
import { toast } from "react-hot-toast";

const templatesApiBaseUrl = process.env.REACT_APP_TEMPLATES_API_URL;

type GetTemplatesParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  channels?: string[] | string;
};

type PagedTemplatesResponse<T = any> = {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
} | T[];

export const getCommunicationTemplates = async (params?: GetTemplatesParams): Promise<PagedTemplatesResponse> => {
  try {
    const response = await axios.get(`${templatesApiBaseUrl}/templates`, {
      params: {
        page: params?.page,
        pageSize: params?.pageSize,
        search: params?.search,
        // Send channels either as provided string or comma-separated list
        channels: Array.isArray(params?.channels) ? params?.channels.join(",") : params?.channels,
      },
    });
    return response.data;
  } catch (error: any) {
    toast.error(`Failed to fetch templates: ${error?.response?.data?.message || error?.message || "Unknown error"}`);
    throw error;
  }
};

export const getCommunicationTemplateById = async (id: string): Promise<any> => {
  try {
    const response = await axios.get(`${templatesApiBaseUrl}/templates/${encodeURIComponent(id)}`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

/** Matches `Communication_Automation/backend/src/models/Template.ts` */
export type CreateTemplatePayload = {
  templateCode: string;
  name: string;
  channel: string;
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  language?: string;
  type?: "TEXT" | "DOCUMENT" | "IMAGE" | "VIDEO";
  header?: string | null;
  body: string;
  footer?: string | null;
  params?: string[];
  status?: "ENABLED" | "DISABLED" | "PAUSED" | "REJECTED";
  buttonType?: "NONE" | "STATIC" | "DYNAMIC";
  buttons?: unknown[];
};

export const createCommunicationTemplate = async (payload: CreateTemplatePayload) => {
  try {
    const response = await axios.post(`${templatesApiBaseUrl}/templates`, payload);
    return response.data;
  } catch (error: any) {
    toast.error(`Failed to create template: ${error?.response?.data?.message || error?.message || "Unknown error"}`);
    throw error;
  }
};

/** Pull templates from the upstream provider / source of truth (backend implements POST /templates/sync). */
export const syncCommunicationTemplates = async (payload?: Record<string, unknown>) => {
  try {
    const response = await axios.post(`${templatesApiBaseUrl}/templates/sync`, payload ?? {});
    return response.data;
  } catch (error: any) {
    toast.error(`Failed to sync templates: ${error?.response?.data?.message || error?.message || "Unknown error"}`);
    throw error;
  }
};

